import { useState } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

// Routes
import { ALBUM } from '@/lib/routes/apiEndpoints';
import { CONTENT_ALBUMS } from '@/routes/privateRoutes';

// Utils
import { deleteApi } from '@/utils/client';

// Hooks
import useAlbums from '@/utils/hooks/swr/albums/useAlbums';
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Components
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import InfoBanner from '@/components/base/InfoBanner';

const DeleteAlbumContent = ({
    albumId,
    toggle = () => {
        /** */
    }
}) => {
    const router = useRouter();
    const [status, setStatus] = useState({
        isSubmitting: false,
        error: null
    });
    const { t } = useTranslation('content');
    const { activeSocialProfile } = useActiveSocialProfile();
    const { mutateAlbums } = useAlbums({
        activeSPID: activeSocialProfile?.id,
        limit: 10,
        options: { revalidateOnFocus: false, revalidateOnReconnect: false }
    });

    const onDelete = async () => {
        setStatus({
            isSubmitting: true,
            error: null
        });
        try {
            injectEndpointUrlParams;
            await deleteApi(
                injectEndpointUrlParams(ALBUM, {
                    SPID: activeSocialProfile?.id,
                    ALBUM_ID: albumId
                })
            );
            await mutateAlbums();
            await router.push(CONTENT_ALBUMS);
        } catch (e) {
            setStatus((prevState) => ({
                ...prevState,
                isSubmitting: false,
                error: t('errors:error_occurred_delete_data')
            }));
        }
    };

    return (
        <Modal.Body>
            {status?.error ? (
                <InfoBanner
                    className="mb-4"
                    title={t('modal.delete_error')}
                    message={status?.error}
                    onClose={() => {
                        setStatus((prevState) => ({
                            ...prevState,
                            error: null
                        }));
                    }}
                />
            ) : null}
            <Modal.Title>{t('modal.delete_album_confirm')}</Modal.Title>
            <Modal.Content className="pt-m text-center font-GMedium text-icoGray-500 dark:text-icoDarkMode-wolf">
                {t('modal.delete_album_confirm_content')}
            </Modal.Content>
            <Modal.Footer className="flex flex-col items-center justify-between gap-3 pb-12 pt-8 xs:flex-row">
                <Button
                    variant="secondary"
                    className="w-full"
                    disabled={status?.isSubmitting}
                    onClick={toggle}>
                    {t('commons:no')}
                </Button>
                <Button isLoading={status?.isSubmitting} className="w-full" onClick={onDelete}>
                    {t('commons:yes')}
                </Button>
            </Modal.Footer>
        </Modal.Body>
    );
};

const DeleteAlbumModal = ({
    isOpen,
    toggle = () => {
        /** */
    },
    albumId
}) => {
    return (
        <Modal open={isOpen} onDismiss={toggle}>
            <DeleteAlbumContent toggle={toggle} albumId={albumId} />
        </Modal>
    );
};

export default DeleteAlbumModal;
